import React from 'react';

/**
 * SkeletonLoader - Reusable shimmer loading component
 * @param {string} variant - 'card' | 'list' | 'stat' | 'chart' | 'text' | 'avatar' | 'custom'
 * @param {number} count - Number of skeleton items to render
 * @param {string} className - Additional classes
 */
const SkeletonLoader = ({ variant = 'card', count = 1, className = '' }) => {
    const shimmerClass = 'animate-pulse bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%]';

    const renderSkeleton = (idx) => {
        switch (variant) {
            case 'stat':
                return (
                    <div key={idx} className={`bg-card border border-border rounded-xl p-5 ${className}`}>
                        <div className={`h-3 w-20 ${shimmerClass} rounded mb-3`} />
                        <div className={`h-8 w-16 ${shimmerClass} rounded mb-2`} />
                        <div className={`h-2 w-24 ${shimmerClass} rounded`} />
                    </div>
                );

            case 'chart':
                return (
                    <div key={idx} className={`bg-card border border-border rounded-xl p-6 ${className}`}>
                        <div className={`h-5 w-40 ${shimmerClass} rounded mb-4`} />
                        <div className="flex items-end gap-2 h-32">
                            {Array.from({ length: 7 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`flex-1 ${shimmerClass} rounded-t`}
                                    style={{ height: `${20 + Math.random() * 80}%` }}
                                />
                            ))}
                        </div>
                    </div>
                );

            case 'list':
                return (
                    <div key={idx} className={`flex items-center gap-4 py-3 ${className}`}>
                        <div className={`w-10 h-10 ${shimmerClass} rounded-lg shrink-0`} />
                        <div className="flex-1 space-y-2">
                            <div className={`h-3 w-3/4 ${shimmerClass} rounded`} />
                            <div className={`h-2 w-1/2 ${shimmerClass} rounded`} />
                        </div>
                        <div className={`h-4 w-12 ${shimmerClass} rounded`} />
                    </div>
                );

            case 'text':
                return (
                    <div key={idx} className={`space-y-2 ${className}`}>
                        <div className={`h-3 w-full ${shimmerClass} rounded`} />
                        <div className={`h-3 w-5/6 ${shimmerClass} rounded`} />
                        <div className={`h-3 w-2/3 ${shimmerClass} rounded`} />
                    </div>
                );

            case 'avatar':
                return (
                    <div key={idx} className={`flex items-center gap-3 ${className}`}>
                        <div className={`w-12 h-12 ${shimmerClass} rounded-full shrink-0`} />
                        <div className="space-y-2 flex-1">
                            <div className={`h-4 w-32 ${shimmerClass} rounded`} />
                            <div className={`h-3 w-24 ${shimmerClass} rounded`} />
                        </div>
                    </div>
                );

            case 'card':
            default:
                return (
                    <div key={idx} className={`bg-card border border-border rounded-xl p-6 ${className}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-10 h-10 ${shimmerClass} rounded-lg`} />
                            <div className="space-y-2 flex-1">
                                <div className={`h-4 w-40 ${shimmerClass} rounded`} />
                                <div className={`h-3 w-28 ${shimmerClass} rounded`} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className={`h-3 w-full ${shimmerClass} rounded`} />
                            <div className={`h-3 w-4/5 ${shimmerClass} rounded`} />
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className={count > 1 && variant !== 'list' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-2'}>
            {Array.from({ length: count }).map((_, i) => renderSkeleton(i))}
        </div>
    );
};

export default SkeletonLoader;
